/**
 * Where you are signed in, and getting yourself out of all of it.
 *
 * WHAT THIS CAN AND CANNOT DO, SAID FIRST
 *
 * Firebase gives a browser no way to list a person's sessions and no way to
 * revoke a token. Both are server-only, and there is no server. So this is not
 * a list of sessions read from anywhere authoritative — it is a list this app
 * keeps of the browsers it has been opened in, and signing out everywhere is an
 * INSTRUCTION those browsers obey rather than a revocation.
 *
 * That covers the case people actually have: a laptop left at a client's
 * office, a browser on a machine you no longer use. It does NOT stop somebody
 * who has taken a token and is not running this app. Saying so is the whole
 * difference between a security feature and the appearance of one.
 *
 * A DEVICE, NOT A TAB. The id lives in localStorage, so five tabs on one
 * machine are one entry rather than five — a list where closing and reopening
 * a tab adds a row is a list nobody can read.
 */

export interface Session {
  id: string;
  who: string;
  /** When this browser most recently signed in. */
  startedAt: number;
  /** When it was last seen running. */
  lastSeen: number;
  /** "Chrome on macOS", as near as a browser will admit. */
  device: string;
  /** Which copy of the board — the real one, the test copy, a laptop. */
  where: string;
  /** The system, browser and (hand-helds only) device kind, reported in parts. */
  os?: string;
  browser?: string;
  deviceKind?: string;
  /** How this sign-in was made: "password", "link", or "sso". */
  method?: string;
  /** The raw user-agent, kept for forensics; parsed into the parts above. */
  userAgent?: string;
}

/**
 * WHEN EACH PERSON LAST SIGNED IN — for the People screen (people-access A).
 *
 * A person signs in on several devices, each its own session row; their "last
 * signed in" is the most recent sign-in across all of them. `startedAt` is that
 * sign-in time (falling back to `lastSeen` for an old row that predates it). The
 * email is folded to lower case so one person is one entry. A person with no row
 * is simply absent from the map — the caller shows "—".
 *
 * NOTE: the sessions RLS hands a non-owner admin only their OWN rows, so from
 * this function alone the map covers just the viewer. The roster fills in everyone
 * else from the admin-only RPC `last_sign_in_times` (migration 0210), which
 * returns ONLY an address and a timestamp — never a device or a place — so the
 * sessions table stays private (0002) while the roster still shows each person's
 * last sign-in. Admin.tsx merges the two, the RPC authoritative.
 */
export function latestSignInByPerson(sessions: Session[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of sessions) {
    const who = (s.who ?? '').toLowerCase();
    if (!who) continue;
    const at = s.startedAt || s.lastSeen || 0;
    if (at && at > (m.get(who) ?? 0)) m.set(who, at);
  }
  return m;
}

/**
 * Merge the admin-only RPC's cross-user last-sign-in times (migration 0210) over
 * the viewer's own session-derived times. The RPC is authoritative and also adds
 * everyone else, so an admin sees the whole roster; the viewer's own sessions
 * still cover them when the RPC is empty (a non-admin, or a backend without it).
 * The address is folded to lower case so one person stays one entry.
 */
export function mergeSignInTimes(
  own: Map<string, number>,
  rpc: readonly { email: string; at: number }[]
): Map<string, number> {
  const m = new Map(own);
  for (const r of rpc) {
    const who = (r.email ?? '').toLowerCase();
    if (who && r.at) m.set(who, r.at);
  }
  return m;
}

const DEVICE_KEY = 'flightdeck-device';

/**
 * This browser's id, made once and kept.
 *
 * In localStorage rather than sessionStorage so it survives closing the tab,
 * which is what makes it a device rather than a visit. Private browsing throws
 * on storage, and a throwaway id is better than crashing the sign-in.
 */
export function deviceId(): string {
  try {
    const held = localStorage.getItem(DEVICE_KEY);
    if (held) return held;
    const made = `d${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_KEY, made);
    return made;
  } catch {
    return `d${Math.random().toString(36).slice(2, 10)}`;
  }
}

const METHOD_KEY = 'flightdeck-signin-method';

/**
 * Remember HOW this sign-in was made, for the one write that follows it.
 *
 * The auth layer knows the method — a password, a sign-in link — but the
 * session row is written a beat later by a different piece of code reacting to
 * the auth-state change, which does not. So the method is left here at the
 * moment it is known and taken once when the row is written. localStorage, not a
 * variable, because a sign-in link completes on a fresh page load after the
 * redirect, and a variable set before it would be gone.
 */
export function rememberSignInMethod(method: string): void {
  try {
    localStorage.setItem(METHOD_KEY, method);
  } catch {
    /* private browsing has no storage; the method is simply not recorded */
  }
}

/** Take the remembered method and forget it, so it marks one sign-in only. */
export function takeSignInMethod(): string {
  try {
    const held = localStorage.getItem(METHOD_KEY) ?? '';
    if (held) localStorage.removeItem(METHOD_KEY);
    return held;
  } catch {
    return '';
  }
}

/**
 * What to call this browser, from what it admits to being.
 *
 * Deliberately coarse. A user agent string is long, mostly lies for
 * compatibility reasons, and the only useful question is "which of my machines
 * is this". Chrome claims to be Safari; Edge claims to be Chrome; both are
 * handled by asking in the right order rather than by trusting any one name.
 */
export function describeDevice(ua: string): string {
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'a browser';

  const os = /iPhone|iPad|iPod/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Mac OS X|Macintosh/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'an unknown system';

  return `${browser} on ${os}`;
}

/**
 * The same browser, reported in SEPARATE parts for the History drill: the
 * operating system, the browser (with a version where the string gives one),
 * and a device kind that only earns a place when it says more than the system
 * already does.
 *
 * HONEST ABOUT WHAT A BROWSER WITHHOLDS. A version is included only when it is
 * actually in the user-agent string: iOS and Android name theirs, and every
 * browser names its own, so those come through; macOS freezes its version and
 * Windows cannot be told from 10 to 11 in the string, so no version is invented
 * for them. A part that cannot be read is returned empty, and the History line
 * and the sign-in drill both drop an empty part rather than printing a blank.
 *
 * DEVICE KIND is left empty on a desktop — "Mac" beside "macOS", or "Windows
 * PC" beside "Windows", only repeats the system. It is filled only for the
 * hand-held shapes a browser does name (iPhone, iPad, an Android phone or
 * tablet), where it genuinely adds something the OS line does not.
 */
export interface DeviceParts {
  deviceKind: string;
  os: string;
  browser: string;
}

export function describeDeviceParts(ua: string): DeviceParts {
  const ver = (re: RegExp): string => {
    const m = ua.match(re);
    return m && m[1] ? ` ${m[1]}` : '';
  };

  // Order matters: Edge and Opera both carry "Chrome/…", and Chrome carries
  // "Safari/…", so the most specific token is asked for first.
  const browser = /Edg\//.test(ua)
    ? `Edge${ver(/Edg\/(\d+)/)}`
    : /OPR\//.test(ua)
      ? `Opera${ver(/OPR\/(\d+)/)}`
      : /Firefox\//.test(ua)
        ? `Firefox${ver(/Firefox\/(\d+)/)}`
        : /Chrome\//.test(ua)
          ? `Chrome${ver(/Chrome\/(\d+)/)}`
          : /Safari\//.test(ua)
            ? `Safari${ver(/Version\/(\d+)/)}`
            : '';

  const os = /iPhone|iPad|iPod/.test(ua)
    ? `iOS${ver(/OS (\d+)/)}`
    : /Android/.test(ua)
      ? `Android${ver(/Android (\d+)/)}`
      : /Mac OS X|Macintosh/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';

  const deviceKind = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /iPod/.test(ua)
        ? 'iPod'
        : /Android/.test(ua)
          ? /Mobile/.test(ua)
            ? 'Android phone'
            : 'Android tablet'
          : '';

  return { deviceKind, os, browser };
}

/**
 * Whether a session should take itself off.
 *
 * The instruction is a moment in time rather than a list of what to end, so it
 * cannot be raced: anything that STARTED before it goes, anything started
 * after is by definition a fresh sign-in and stays. That is also what stops
 * the browser you pressed the button in from signing itself out — it started
 * before, so it goes too, which is correct: "everywhere" includes here.
 */
export function shouldSignOut(startedAt: number, signOutAfter: number | undefined): boolean {
  return !!signOutAfter && startedAt <= signOutAfter;
}

/** Newest first, and this browser always at the top of its own list. */
export function orderSessions(rows: Session[], thisDevice: string): Session[] {
  return [...rows].sort((a, b) => {
    if (a.id === thisDevice) return -1;
    if (b.id === thisDevice) return 1;
    return b.lastSeen - a.lastSeen;
  });
}
